export type AudioDto = {
  id: string;
  name: string;
  prompt: string;
  is_generated: boolean;
  is_liked?: boolean;
  is_published?: boolean;
  is_user_upload?: boolean;
  created_at?: string;
  is_deleted?: boolean;
  is_hide?: boolean; // trigger remove animation
  duration: number;
  plays?: number;
  metadata?: {
    extendedIds?: string[];
    validate_status?: GenerateStatus;
    type?: string;
    reimagineId?: string;
    is_bonus?: boolean;
  };
  track_group?: {
    name: string;
    id: number;
  };
  stylefilter?: string;
  stylefilterDetail?: StylefilterDetailDto;
  user_name?: string;
  user?: {
    name: string;
  };
  sign_url?: string;
  url?: string;
  status?: GenerateStatus;
  folder_id: number;
  hasError?: boolean;
  error_message?: boolean;
  source_url?: string;
  tracksNum?: number;
  creditBalance?: number;
  is_bonus?: boolean;
};

export type StylefilterDetailDto = {
  artist_name?: string;
  code?: string;
  name?: string;
  album_cover?: string;
  thumb_album_cover?: string;
};

export type MarketAudioDto = {
  id: string;
  name: string;
  prompt: string;
  is_generated: boolean;
  is_liked?: boolean;
  is_published?: boolean;
  is_user_upload?: boolean;
  created_at?: string;
  is_deleted?: boolean;
  is_hide?: boolean; // trigger remove animation
  duration: number;
  plays?: number;
  metadata?: {
    extendedIds?: string[];
    validate_status?: GenerateStatus;
    type?: string;
    reimagineId?: string;
    is_bonus?: boolean;
  };
  track_group?: {
    name: string;
    id: number;
  };
  stylefilter: {
    name: string;
    image: string;
    stylefilter_name?: string;
    artist?: string;
  };
  user_name?: string;
  user?: {
    name: string;
  };
  sign_url?: string;
  url?: string;
  status?: GenerateStatus;
  folder_id: number;
  hasError?: boolean;
  error_message?: boolean;
  source_url?: string;
  tracksNum?: number;
  creditBalance?: number;
};

export type MarketAudioDto = {
  id: string;
  name: string;
  prompt: string;
  is_generated: boolean;
  is_liked?: boolean;
  is_published?: boolean;
  is_user_upload?: boolean;
  created_at?: string;
  is_deleted?: boolean;
  is_hide?: boolean; // trigger remove animation
  duration: number;
  plays?: number;
  metadata?: {
    extendedIds?: string[];
    validate_status?: GenerateStatus;
    type?: string;
    reimagineId?: string;
    is_bonus?: boolean;
  };
  track_group?: {
    name: string;
    id: number;
  };
  stylefilter: {
    name: string;
    image: string;
  };
  user_name?: string;
  user?: {
    name: string;
  };
  sign_url?: string;
  url?: string;
  status?: GenerateStatus;
  folder_id: number;
  hasError?: boolean;
  error_message?: boolean;
  source_url?: string;
  tracksNum?: number;
  creditBalance?: number;
};

export type TrackGroupDto = {
  id: number;
  name: string;
  count: number;
  created_at: string;
};

export type GenerateAudioInputParams = {
  duration: number;
  format: string;
  fadeOutLength: number;
  steps?: number;
  prompt: string;
  negativePrompt?: string;
  length: number;
  guidanceScale?: number;
  guidanceRescale?: number;
  seed?: number;
  ragPrompt?: boolean;
  ragAudio2Audio?: boolean;
  ragPromptIndex?: number;
  ragAudioIndex?: number;
  ragPromptTopk?: number;
  ragAudioTopk?: number;
  strength?: number;
  styleFilter?: string;
  filterTI?: number;
  filterWeight?: number;
  scheduler1StepRatio?: number;
  scheduler1Stride?: number;
  model: ModelVersion;
  numberOfAudio: number;
};

export type ContinueAudioInputParams = {
  keep_times: number;
  mean_max_norm: boolean;
  continuation_smooth: boolean;
  format: string;
  fadeOutLength: number;
  duration: number;
  length: number;
  steps?: number;
  prompt: string;
  negativePrompt?: string;
  guidanceScale?: number;
  guidanceRescale?: number;
  seed?: number;
  ragPrompt?: boolean;
  ragAudio2Audio?: boolean;
  ragPromptIndex?: number;
  ragAudioIndex?: number;
  ragPromptTopk?: number;
  ragAudioTopk?: number;
  strength?: number;
  styleFilter?: string;
  filterTI?: number;
  filterWeight?: number;
  model: ModelVersion;
  numberOfAudio: number;
};

export type Audio2AudioInputParams = {
  steps?: number;
  prompt: string;
  negativePrompt?: string;
  guidanceScale?: number;
  guidanceRescale?: number;
  seed?: number;
  ragPrompt?: boolean;
  ragPromptIndex?: number;
  ragPromptTopk?: number;
  strength?: number;
  styleFilter?: string;
  filterTI?: number;
  filterWeight?: number;
  scheduler1StepRatio?: number;
  scheduler1Stride?: number;
  model: ModelVersion;
};

export type AudioRefinerInputParams = {
  model: ModelVersion;
  steps: number;
};

export type ModelVersion =
  | 'jenxl_v0318'
  | 'jenxl_v0619'
  | 'jenxl_v0627'
  | 'humming_to_music_v2_1'
  | 'humming_to_music_v2_2'
  | 'refiner_v0619';

export enum GenerateStatus {
  GENERATING = 'generating',
  VALIDATING = 'validating',
  VALIDATED = 'validated',
  GENERATED = 'generated',
}

export type SortOrderType = 'LF' | 'EF' | 'LS' | 'SL' | 'AZ' | 'ZA' | 'SF_ARTIST';

export interface GenerateAudioInputParamsV2 {
  prompt: string;
  trackName?: string;
  folderId?: number;
  translatedPrompt?: string;
  negativePrompt?: string;
  fadeOutLength?: number;
  length?: number;
  steps?: number;
  duration?: number;
  guidanceScale?: number;
  guidanceRescale?: number;
  ragPromptIndex?: number;
  ragAudioIndex?: number;
  ragPromptTopk?: number;
  ragAudioTopk?: number;
  strength?: number;
  scheduler1StepRatio?: number;
  scheduler1Stride?: number;
  format?: string;
  model?: string;
  assist?: boolean;
  styleFilter?: string;
  filterTI?: number;
  filterWeight?: number;
  checkpoint_path?: string;
  keep_times?: number;
  continuation_smooth?: boolean;
  mean_max_norm?: boolean;
  // mean_max_norm: boolean
  tracksNum?: number;
}

export interface ReimagineInputParamsV2 extends GenerateAudioInputParamsV2 {
  uploadFileId: number;
  reimagineType: 'track' | 'instrument';
}
